import { isDefined, isEmptyObject, isNotDefined } from '@sgme/fp';

import { getPreviousValue, getTcValidation } from '@/common/trade-capture/tradeCaptureValidation.ts';
import { isEmptyOrNotDefined } from '@/components/sg-design-system/utils/typescript-helpers.ts';
import type {
  AccumulatorScheduleLineResponse,
  ForwardAccumulatorResponse,
  TcAccumulatorHedgeType,
} from '@/store/forwardAccumulator/api/tradeCapture/response.model.ts';
import type {
  AccumulatorScheduleLine,
  ForwardAccumulatorProperties,
  ForwardAccumulatorTradeCaptureError,
  HedgeType,
  ResponseBarrier,
  ScheduleError,
  TcKey,
} from '@/store/forwardAccumulator/state.ts';
import type { TradeCaptureChecked, TradeCaptureError } from '@/store/types.ts';
import type { TradeCaptureResponseLegErrors } from '@/types/trade-capture.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';
import { strictEntries } from '@/utils/object.ts';
import type { ObjectWithNullToUndefined } from '@/utils/typescript.ts';

export type ForwardAccumulatorTcResponse =
  | {
      type: 'success';
      tradeCapture: TradeCaptureChecked<ForwardAccumulatorProperties, ForwardAccumulatorTradeCaptureError>;
      properties: Partial<ForwardAccumulatorProperties>;
    }
  | { type: 'error'; tradeCapture: TradeCaptureError };

export const mapTcResponse = (
  response: ForwardAccumulatorResponse,
  tileId: string,
  sessionId: string | undefined,
  currencyPair: string | null | undefined,
): ForwardAccumulatorTcResponse => {
  const tradeCaptureOption = response[tileId];
  if (isNotDefined(tradeCaptureOption) || isNotDefined(sessionId)) {
    const tradeCaptureError: TradeCaptureError = {
      status: 'ERROR',
      error: 'SERVER_ERROR',
      // what is the good version here ??
      sessionId: sessionId ?? 'unknown session',
      version: -1,
    };
    return { type: 'error', tradeCapture: tradeCaptureError };
  }

  const { changedFields, errors, warnings } = tradeCaptureOption;
  const { hedgeType, hedgePaymentDate, hedgePrice, hedgePriceString, markupCurrency } = changedFields;

  const errorsForValidation = errors?.legs?.[0];
  const warningsForValidation = warnings?.legs?.[0];

  const validation: ForwardAccumulatorTradeCaptureError = getForwardAccumulatorValidation(
    errorsForValidation,
    warningsForValidation,
  );

  const tradeCapture: TradeCaptureChecked<ForwardAccumulatorProperties, ForwardAccumulatorTradeCaptureError> = {
    status: 'CHECKED',
    sessionId,
    version: tradeCaptureOption.idVersion,

    isReadyToPrice: changedFields.isReadyToPrice ?? false,
    isPriceObsolete: changedFields.isPriceObsolete ?? false,
    isProductObsolete: changedFields.isProductObsolete ?? false,

    validation,
  };

  const {
    strikeComparator,
    premiumPaymentCurrency,
    premiumPaymentDate,
    premiumPaymentDateTenor,
    side,
    amount,
    amountSplitType,
    negotiatedCurrency,
    leverage,
    leverageAmount,
    strike,
    barrier,
    possibleKoPaymentConventions,
    koPaymentConvention,
    firstFixingDate,
    firstFixingDateTenor,
    possibleFixingFrequencies,
    possibleSettlementFrequencies,
    fixingFrequency,
    numberOfFixings,
    numberOfSettlements,
    possibleFixingReferences1,
    fixingReference1,
    possibleFixingReferences2,
    fixingReference2,
    settlementFrequency,
    cashSettlementCurrency,
    expiryDateTenor,
    expiryDate,
    deliveryDate,
    customFields,
    lastFixingDate,
    lastFixingDateTenor,
    calendar,
    cutOffMarketPlace,
    totalLeverageAmount,
    totalNotionalAmount,
    isCrossed,
    crossCurrency,
    possibleCrossCurrencies,
    summary,
    step,
    stepString,
    step2,
    step2String,
    hedgeAmountInCcy1,
    hedgeAmountInCcy2,
    accuType,
    scheduleCustomFields,
    schedule,
    stepUp,
    stepDown,
  } = changedFields.legs[0];

  const scheduleData = isNotDefined(errors?.legs?.[0]?.schedule)
    ? schedule
    : (errors?.legs?.[0]?.schedule?.[0]?.previousValue as Record<string, AccumulatorScheduleLineResponse> | undefined);

  const properties: Partial<ForwardAccumulatorProperties> = withoutNullish<ForwardAccumulatorProperties>({
    currencyPair: changedFields.currencyPair,
    hedgeType: mapToHedgeType(hedgeType),
    hedgePaymentDate,
    accuType,
    hedgeAmount: mapHedgeAmount(hedgeAmountInCcy1, hedgeAmountInCcy2),
    spotHedge: mapSpotHedge(hedgePrice, hedgePriceString),
    premiumDate: premiumPaymentDate ?? getPreviousValue(validation.premiumDate),
    premiumDateTenor: premiumPaymentDateTenor,
    clientsWay: side,
    notional: amount ?? getPreviousValue(validation.notional),
    amountMode: amountSplitType,
    notionalCurrencyIndex: negotiatedCurrency,
    leverage: leverage ?? getPreviousValue(validation.leverage),
    leverageAmount: leverageAmount ?? getPreviousValue(validation.leverageAmount),
    premiumTypeCurrencyIndex: mapToPriceCurrency(premiumPaymentCurrency, currencyPair),
    strike: strike ?? getPreviousValue(validation.strike),
    step: (step ?? stepString) ? (step ?? Number(stepString)) : getPreviousValue(validation.step),
    ekiTrigger: (step2 ?? step2String) ? (step2 ?? Number(step2String)) : getPreviousValue(validation.ekiTrigger),
    akoTrigger: barrier?.level ? barrier?.level : undefined,
    summary,
    barrier: isDefined(barrier)
      ? ({
          level: barrier?.level,
          nature: barrier?.nature,
          type: barrier?.type,
        } as ResponseBarrier)
      : undefined,
    firstFixingDate: firstFixingDate || getPreviousValue(validation.firstFixingDate),
    firstFixingTenor: firstFixingDateTenor,
    targetProfitType: koPaymentConvention ?? possibleKoPaymentConventions?.default,
    targetProfitTypeList: possibleKoPaymentConventions?.possible,
    numberOfFixings,
    numberOfSettlements,
    fixingFrequency: fixingFrequency ?? possibleFixingFrequencies?.default,
    fixingFrequenciesList: possibleFixingFrequencies?.possible,
    fixingReference1: fixingReference1,
    fixingReference1Default: possibleFixingReferences1?.default,
    fixingReference1List: possibleFixingReferences1?.possible,
    fixingReference2: fixingReference2,
    fixingReference2Default: possibleFixingReferences2?.default,
    fixingReference2List: possibleFixingReferences2?.possible,
    settlementFrequency,
    settlementFrequencyList: possibleSettlementFrequencies?.possible, // used ?? not sure
    cashSettlementCurrency,
    lastFixingTenor: lastFixingDateTenor,
    lastFixingDate: lastFixingDate ?? getPreviousValue(validation?.lastFixingDate),
    schedule: isDefined(scheduleData) ? mapToTradeCaptureSchedule(scheduleData) : undefined,
    totalLeverage: totalLeverageAmount,
    totalNotional: totalNotionalAmount,
    scheduleCustomFields,
    markupCurrency,
    isCrossed,
    crossCurrency: crossCurrency ?? possibleCrossCurrencies?.default,
    crossCurrenciesList: possibleCrossCurrencies?.possible,
    customFields,
    //This is just for MP changes
    stepDown,
    stepUp,
    expiryDateTenor,
    expiryDate,
    deliveryDate,
    calendar: calendar ?? errors?.legs?.[0]?.calendarChanges?.[0]?.previousValue,
    cutOffMarketPlace,
    strikeComparator,
  });

  return { type: 'success', tradeCapture, properties };
};

/**
 * Transforms an object to remove undefined values and convert null values to undefined.
 *
 * This is useful to transform TC responses where null values are used to indicate that a field is not set.
 * The object can also have undefined values because they are just missing from the response.
 * Those undefined values are filtered out.
 *
 * @param param
 */
function withoutNullish<T extends object>(param: { [K in keyof T]?: T[K] | null }): ObjectWithNullToUndefined<T> {
  return Object.fromEntries(
    Object.entries(param)
      .map(([key, value]) => {
        // convert null to undefined
        if (value === null) {
          return [key, undefined];
        }
        // filter undefined values
        if (value === undefined) {
          return undefined;
        }
        return [key, value];
      })
      .filter(isDefined),
  ) as ObjectWithNullToUndefined<T>;
}

function mapToHedgeType(value: TcAccumulatorHedgeType | null | undefined): HedgeType | undefined {
  if (value === 'None' || value === null) {
    return 'Live';
  }
  return value;
}

function mapHedgeAmount(hedgeAmountInCcy1: number | null | undefined, hedgeAmountInCcy2: number | null | undefined) {
  // if one amount is null and the other is null or undefined
  // CAUTION : isDefined uses double equal not triple equals, meaning that undefined is also considered truthy here
  return (hedgeAmountInCcy1 === null && !isDefined(hedgeAmountInCcy2)) ||
    (hedgeAmountInCcy2 === null && !isDefined(hedgeAmountInCcy1))
    ? null
    : (hedgeAmountInCcy1 ?? hedgeAmountInCcy2);
}

const mapToPriceCurrency = (
  premiumPaymentCurrency: string | undefined,
  currencyPair: string | null | undefined,
): CurrencyIndex | undefined => {
  if (isNotDefined(currencyPair) || isEmptyOrNotDefined(premiumPaymentCurrency)) {
    return undefined;
  }

  const [currency1, currency2] = currencyPair.split('/');
  switch (premiumPaymentCurrency) {
    case currency1:
      return 1;
    case currency2:
      return 2;
    default:
      return undefined;
  }
};

function mapSpotHedge(
  hedgePrice: number | null | undefined,
  hedgePriceString: string | null | undefined,
): number | null | undefined {
  if (hedgePrice === null) {
    return null;
  }
  if (isDefined(hedgePrice)) {
    return Number(hedgePrice);
  }

  if (isDefined(hedgePriceString)) {
    return Number(hedgePriceString);
  }
  return undefined;
}

function mapToTradeCaptureSchedule(
  schedule: Record<string, AccumulatorScheduleLineResponse | null> | null | undefined,
): Record<string, AccumulatorScheduleLine> | null | undefined {
  if (schedule === undefined || schedule === null || isEmptyObject(schedule)) {
    return null;
  }

  const result = Object.entries(schedule).reduce(
    (acc, [key, leg]) => {
      if (isNotDefined(leg)) {
        return acc;
      }

      acc[key] = {
        fixingDate: leg?.fixingDate,
        paymentDate: isDefined(leg?.paymentDate) ? leg.paymentDate : null,
        strike: leg?.strike,
        // strikeDown: leg?.strikeDown,
        // ekiDown: leg?.stepDown,
        // ekiUp: leg?.stepUp,
        // strikeUp: leg?.strikeUp,
        step2: leg?.step2,
        //  pivot: leg?.step,
        amount: leg?.amount,
        step: leg?.step,
        leverageAmount: leg?.leverageAmount,
      };
      return acc;
    },
    {} as Record<string, AccumulatorScheduleLine>,
  );

  return Object.keys(result).length === 0 ? undefined : result;
}

const getForwardAccumulatorValidation = (
  errorsForValidation: TradeCaptureResponseLegErrors | undefined,
  warningsForValidation: TradeCaptureResponseLegErrors | undefined,
): ForwardAccumulatorTradeCaptureError => {
  const forwardAccumulatorPropertyKeys = [
    ...Object.keys(errorsForValidation ?? {}),
    ...Object.keys(warningsForValidation ?? {}),
  ];
  return forwardAccumulatorPropertyKeys?.reduce((acc, key) => {
    switch (key) {
      case 'hedgeAmountInCcy1':
        acc.hedgeAmount = getTcValidation(
          errorsForValidation?.hedgeAmountInCcy1,
          warningsForValidation?.hedgeAmountInCcy1,
        );
        return acc;
      case 'hedgeAmountInCcy2':
        acc.hedgeAmount = getTcValidation(
          errorsForValidation?.hedgeAmountInCcy2,
          warningsForValidation?.hedgeAmountInCcy2,
        );
        return acc;
      case 'hedgePrice':
        acc.spotHedge = getTcValidation(errorsForValidation?.hedgePrice, warningsForValidation?.hedgePrice);
        return acc;
      case 'premiumPaymentDate':
        acc.premiumDate = getTcValidation(
          errorsForValidation?.premiumPaymentDate,
          warningsForValidation?.premiumPaymentDate,
        );
        return acc;
      case 'side':
        acc.clientsWay = getTcValidation(errorsForValidation?.side, warningsForValidation?.side);
        return acc;
      case 'calendarChanges':
        acc.calendar = getTcValidation(errorsForValidation?.calendarChanges, warningsForValidation?.calendarChanges);
        return acc;
      case 'schedule': {
        const errorsByLeg: Record<string, ScheduleError> = {};
        const scheduleErrors = errorsForValidation?.schedule ?? warningsForValidation?.schedule ?? [];
        const isError = isDefined(errorsForValidation?.schedule);

        for (const scheduleError of scheduleErrors) {
          const scheduleFaultyValues: Record<
            string,
            TcKey<AccumulatorScheduleLineResponse>
          > = scheduleError?.faultyValue ?? {};

          for (const [key, errors] of strictEntries(scheduleFaultyValues)) {
            const legId = key;
            errorsByLeg[legId] = {};

            for (const [fieldKey, faultyValue] of strictEntries(errors)) {
              const previousValue = scheduleError?.previousValue?.[legId]?.[fieldKey.replace('String', '')];

              errorsByLeg[legId][fieldKey] = {
                description: scheduleError?.description,
                id: scheduleError?.id,
                previousValue,
                faultyValue,
                type: isError ? 'error' : 'warning',
              };
            }
          }
        }
        acc.schedule = errorsByLeg;
        return acc;
      }

      case 'amount':
        acc.notional = getTcValidation(errorsForValidation?.amount, warningsForValidation?.amount);
        return acc;
      case 'step2':
        acc.ekiTrigger = getTcValidation(errorsForValidation?.step2, warningsForValidation?.step2);
        return acc;
      case 'barrier':
        acc.akoTrigger = getTcValidation(
          (errorsForValidation?.barrier as TradeCaptureResponseLegErrors | undefined)?.level,
          (warningsForValidation?.barrier as TradeCaptureResponseLegErrors | undefined)?.level,
        );
        return acc;
      default: {
        const key1 = key as keyof Omit<ForwardAccumulatorProperties, 'schedule'>;
        acc[key1] = getTcValidation(errorsForValidation?.[key], warningsForValidation?.[key]) ?? [];
        return acc;
      }
    }
  }, {} as ForwardAccumulatorTradeCaptureError);
};
